import type { Editor, NodeViewRendererProps } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { CODE_LANGUAGES, canonicalLanguage } from '../lib/highlight';

/**
 * NodeView for CodeBlockLowlight — a header strip holding a GitLab-style language
 * dropdown, then the `<pre><code>` that lowlight decorates. Picking a language sets the
 * node's `language` attr, which drives both live highlighting and the fenced-markdown
 * info string (```json) on save.
 *
 * Plain DOM on purpose, not a React NodeView: a React NodeView mounts its content DOM
 * asynchronously, so text typed right after creating the block (toolbar button, or
 * ```ts + Enter) had no `<code>` to land in and ended up in a paragraph after it, with
 * "Applying a mismatched transaction" in the console. This one is synchronous.
 */
export function createCodeBlockView({ node, editor, getPos }: NodeViewRendererProps) {
  const dom = document.createElement('div');
  dom.className = 'code-block';
  const head = document.createElement('div');
  head.className = 'code-block-head';
  head.contentEditable = 'false';
  const select = document.createElement('select');
  select.className = 'code-lang';
  select.setAttribute('aria-label', 'Code block language');
  head.append(select);
  const pre = document.createElement('pre');
  const code = document.createElement('code');
  pre.append(code);
  // The picker goes AFTER the code in the DOM (CSS `order` shows it on top): with a
  // contenteditable=false element first, Chromium put the caret of an EMPTY block
  // outside <code>, and the first keystroke replaced the block with a paragraph.
  dom.append(pre, head);

  let current: PMNode = node as PMNode;
  const ed = editor as Editor;

  const render = () => {
    const tag: string = current.attrs.language ?? '';
    // ```ts selects "TypeScript"; picking the same entry again does not rewrite the tag
    const language = canonicalLanguage(tag);
    const opts = CODE_LANGUAGES.some((l) => l.value === language)
      ? CODE_LANGUAGES
      : [{ value: language, label: language }, ...CODE_LANGUAGES];
    select.replaceChildren(
      ...opts.map((l) => {
        const o = document.createElement('option');
        o.value = l.value;
        o.textContent = l.label;
        return o;
      }),
    );
    select.value = language;
    select.disabled = !ed.isEditable;
  };
  render();

  // don't let the click move the editor selection / focus the doc
  select.addEventListener('mousedown', (e) => e.stopPropagation());
  select.addEventListener('change', () => {
    const pos = typeof getPos === 'function' ? getPos() : undefined;
    if (typeof pos !== 'number') return;
    ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, language: select.value }));
  });
  const syncEditable = () => {
    select.disabled = !ed.isEditable;
  };
  ed.on('update', syncEditable);

  return {
    dom,
    contentDOM: code,
    update(n: PMNode) {
      if (n.type !== current.type) return false;
      const changed = n.attrs.language !== current.attrs.language;
      current = n;
      if (changed) render();
      return true;
    },
    stopEvent: (e: Event) => head.contains(e.target as Node),
    ignoreMutation: (m: MutationRecord | { type: 'selection'; target: Node }) => head.contains(m.target),
    destroy: () => ed.off('update', syncEditable),
  };
}
